import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, Heart, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { api } from "@/lib/api";

const LINKS = [
  { label: "Home", to: "/", testid: "nav-link-home" },
  {
    label: "Gemstone Jewellery",
    to: "/shop?category=Gemstone%20Jewellery",
    testid: "nav-link-gemstone-jewellery",
    children: ["Necklace", "Bracelet", "Earrings", "Pendants", "Artifacts"],
  },
  {
    label: "Fashion Jewellery",
    to: "/shop?category=Fashion%20Jewellery",
    testid: "nav-link-fashion-jewellery",
    children: ["Necklace", "Bracelet", "Earrings", "Pendants"],
  },
  { label: "New Arrivals", to: "/shop?category=New%20Arrivals", testid: "nav-link-new-arrivals" },
];

const subcatUrl = (category, sub) =>
  `/shop?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(sub)}`;

const Header = () => {
  const { count, openCart } = useCart();
  const { count: wishCount } = useWishlist();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    api
      .get("/settings/site")
      .then((r) => setLogoUrl(r.data.logo_url || ""))
      .catch(() => {});
  }, []);

  return (
    <header
      data-testid="header-navigation"
      className="sticky top-0 z-50 border-b border-gold/20 bg-cream/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo-aarmira" className="group flex items-center gap-3">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Aarmira logo"
              data-testid="nav-logo-image"
              className="h-16 w-16 rounded-full border-2 border-gold/40 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}
          <span className="flex flex-col leading-none">
            <span className="font-accent text-2xl font-bold tracking-[0.28em] text-[#8C6D31] transition-colors duration-300 group-hover:text-gold-accent sm:text-3xl">
              AARMIRA
            </span>
            <span className="mt-1 font-mono text-xs font-bold uppercase tracking-[0.28em] text-gold-muted">
              Fashion &amp; Fortune
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <div
              key={l.testid}
              className="relative"
              onMouseEnter={() => l.children && setOpenMenu(l.label)}
              onMouseLeave={() => l.children && setOpenMenu(null)}
            >
              <Link
                to={l.to}
                data-testid={l.testid}
                onClick={() => setOpenMenu(null)}
                className="relative flex items-center gap-1.5 font-sans text-sm font-bold uppercase tracking-[0.14em] text-gold-accent transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-0 after:bg-gold-accent after:transition-[width] after:duration-300 hover:text-espresso hover:after:w-full"
              >
                {l.label}
                {l.children && (
                  <ChevronDown
                    size={13}
                    className={`transition-transform duration-300 ${openMenu === l.label ? "rotate-180" : ""}`}
                  />
                )}
              </Link>
              <AnimatePresence>
                {l.children && openMenu === l.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    data-testid={`nav-dropdown-${l.testid.replace("nav-link-", "")}`}
                    className="absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3"
                  >
                    <div className="border border-gold/30 bg-cream py-2 shadow-[0_24px_48px_-20px_rgba(28,22,19,0.35)]">
                      <div className="mx-auto -mt-3.5 h-2.5 w-2.5 rotate-45 border-l border-t border-gold/30 bg-cream" />
                      {l.children.map((s) => (
                        <Link
                          key={s}
                          to={subcatUrl(l.label, s)}
                          data-testid={`nav-sublink-${l.testid.replace("nav-link-", "")}-${s.toLowerCase()}`}
                          onClick={() => setOpenMenu(null)}
                          className="block px-5 py-2.5 text-sm font-medium tracking-wide text-espresso/75 transition-colors duration-200 hover:bg-gold/10 hover:text-gold-accent"
                        >
                          {s}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/wishlist"
            data-testid="nav-wishlist-link"
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-espresso/10 text-espresso transition-all duration-300 hover:border-gold hover:bg-gold/10 active:scale-95"
            aria-label="Wishlist"
          >
            <Heart size={18} strokeWidth={1.5} />
            {wishCount > 0 && (
              <span
                data-testid="nav-wishlist-count-badge"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-espresso font-mono text-[10px] font-medium text-gold"
              >
                {wishCount}
              </span>
            )}
          </Link>
          <button
            data-testid="nav-cart-trigger-button"
            onClick={openCart}
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-gold bg-gold text-espresso shadow-sm transition-all duration-300 hover:bg-gold-accent active:scale-95"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} strokeWidth={1.8} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-espresso font-mono text-[10px] font-medium text-gold"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMenuOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-espresso/10 text-espresso lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-espresso px-8 py-6"
          >
            <div className="flex items-center justify-between">
              <span className="font-accent text-xl font-bold tracking-[0.35em] text-cream">AARMIRA</span>
              <button
                data-testid="nav-mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.testid}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setMenuOpen(false)}
                    className="font-serif text-3xl text-cream/90 transition-colors hover:text-gold"
                  >
                    {l.label}
                  </Link>
                  {l.children && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {l.children.map((s) => (
                        <Link
                          key={s}
                          to={subcatUrl(l.label, s)}
                          data-testid={`mobile-nav-sublink-${l.testid.replace("nav-link-", "")}-${s.toLowerCase()}`}
                          onClick={() => setMenuOpen(false)}
                          className="border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-espresso"
                        >
                          {s}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </nav>
            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.3em] text-gold-muted lg:mt-auto">
              Fashion &amp; Fortune
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
